import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Backdrop,
  Box,
  Fab,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { Rnd } from 'react-rnd';

import ChatbotUI from './pages/ChatbotUI';
import xtramLogo from './assets/logo.png';
import xtramLogoWhite from './assets/logo-white.png';
import {
  CHAT_DOCK_EDGE,
  CHAT_DOCK_FAB_SIZE,
  chatDockCompactPanelBottomPx,
  chatDockFabBottomPx,
} from './chatDockLayout';
import { CHATBOT_HE } from './chatbotStrings.he';

const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const MAX_WIDTH = 800;
const MAX_HEIGHT = 900;

const DOCK_Z = 13000;

/** Pulsing ring — “AI” assistant affordance */
const fabRingPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(48, 79, 255, 0.5), 0 4px 14px rgba(0, 0, 0, 0.18); }
  55% { box-shadow: 0 0 0 12px rgba(48, 79, 255, 0), 0 6px 20px rgba(0, 0, 0, 0.12); }
`;

const fabFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const aiSparkle = keyframes`
  0% { transform: rotate(0deg) scale(1); opacity: 0.85; }
  50% { transform: rotate(180deg) scale(1.12); opacity: 1; }
  100% { transform: rotate(360deg) scale(1); opacity: 0.85; }
`;

export default function ChatbotWidget() {
  const { i18n } = useTranslation();
  // Use the app's explicit RTL check (matches useDirectionByLanguage). i18n.dir()
  // is unreliable here — it can report 'rtl' for languages like Russian.
  const isRtl = i18n.language === 'he' || i18n.language === 'ar';
  /** Dock on the left for RTL, right for LTR (viewport; portal avoids transformed ancestors).
   *  Stays stacked with the Help button. */
  const horizontalDockSx = isRtl
    ?{ right: CHAT_DOCK_EDGE, left: 'auto' }
    : { right: CHAT_DOCK_EDGE, left: 'auto' };

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [size, setSize] = useState({ width: 380, height: 500 });

  const [autoTip, setAutoTip] = useState(false);
  const [hoverTip, setHoverTip] = useState(false);

  const toggleChat = () => setIsOpen((v) => !v);
  const closeChat = () => {
    setIsOpen(false);
    setIsExpanded(false);
  };

  const toggleExpand = () => setIsExpanded((v) => !v);

  useEffect(() => {
    if (isOpen) {
      setAutoTip(false);
      setHoverTip(false);
      return;
    }
    const show = setTimeout(() => setAutoTip(true), 800);
    const hide = setTimeout(() => setAutoTip(false), 800 + 6500);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, [isOpen]);

  const expandedSx = useMemo(
    () => ({
      position: 'fixed',
      top: { xs: 16, md: 40 },
      left: { xs: 16, md: 40 },
      right: { xs: 16, md: 40 },
      bottom: { xs: 16, md: 40 },
      zIndex: DOCK_Z,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 3,
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
    }),
    []
  );

  const compactPaperSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      borderRadius: 3,
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      bgcolor: '#fff',
    }),
    []
  );

  const Header = (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        bgcolor: '#304fff',
        borderBottom: '1px solid #304fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Box
          component="img"
          src={xtramLogoWhite}
          alt="Xtram"
          sx={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0 }} dir="rtl">
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#fff', textAlign: 'right' }} noWrap>
            {CHATBOT_HE.titleMain}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.8)', textAlign: 'right' }} noWrap>
            {CHATBOT_HE.titleSub}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
       
        <IconButton
          onClick={toggleExpand}
          size="small"
          sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
          aria-label={isExpanded ? CHATBOT_HE.ariaCollapse : CHATBOT_HE.ariaExpand}
        >
          {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
        </IconButton>

        <IconButton
          onClick={closeChat}
          size="small"
          sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
          aria-label={CHATBOT_HE.ariaCloseChat}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  const tooltipOpen = !isOpen && (autoTip || hoverTip);

  /** Portal to body so `position:fixed` + `left` use the viewport (not a transformed layout ancestor). */
  return createPortal(
    <>
      {isOpen && isExpanded ? (
        <Backdrop
          open
          onClick={closeChat}
          sx={{
            zIndex: DOCK_Z - 1,
            bgcolor: 'rgba(0,0,0,0.30)',
            backdropFilter: 'blur(4px)',
          }}
        />
      ) : null}

      {isOpen && !isExpanded ? (
        <Box
          sx={{
            position: 'fixed',
            zIndex: DOCK_Z,
            bottom: chatDockCompactPanelBottomPx,
            ...horizontalDockSx,
            width: size.width,
            height: size.height,
            maxWidth: `calc(100vw - ${CHAT_DOCK_EDGE * 2}px)`,
            maxHeight: `calc(100vh - ${chatDockCompactPanelBottomPx + CHAT_DOCK_EDGE}px)`,
          }}
        >
          <Rnd
            size={{ width: size.width, height: size.height }}
            minWidth={MIN_WIDTH}
            minHeight={MIN_HEIGHT}
            maxWidth={MAX_WIDTH}
            maxHeight={MAX_HEIGHT}
            position={{ x: 0, y: 0 }}
            bounds="window"
            onResizeStop={(e, direction, ref) => {
              setSize({ width: ref.offsetWidth, height: ref.offsetHeight });
            }}
            enableResizing={{
              top: true,
              right: true,
              bottom: true,
              left: true,
              topRight: true,
              bottomRight: true,
              bottomLeft: true,
              topLeft: true,
            }}
            dragHandleClassName="xtram-chatbot-drag-handle"
            style={{ position: 'relative', width: '100%', height: '100%' }}
          >
            <Paper elevation={24} sx={compactPaperSx}>
              <Box className="xtram-chatbot-drag-handle" sx={{ cursor: 'move' }}>
                {Header}
              </Box>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ChatbotUI compact hideSidebar />
              </Box>
            </Paper>
          </Rnd>
        </Box>
      ) : null}

      {isOpen && isExpanded ? (
        <Paper elevation={24} sx={expandedSx}>
          {Header}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ChatbotUI compact={false} hideSidebar />
          </Box>
        </Paper>
      ) : null}

      <Tooltip
        title={
          <span dir="rtl" style={{ display: 'block', textAlign: 'right', unicodeBidi: 'plaintext' }}>
            {CHATBOT_HE.launcherTooltip}
          </span>
        }
        placement={isRtl ? 'right' : 'left'}
        arrow
        open={tooltipOpen}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        slotProps={{
          tooltip: {
            sx: {
              direction: 'rtl',
              fontSize: '0.95rem',
              fontWeight: 600,
              bgcolor: 'grey.900',
              textAlign: 'right',
              '& .MuiTooltip-arrow': { color: 'grey.900' },
            },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            position: 'fixed',
            zIndex: DOCK_Z,
            bottom: chatDockFabBottomPx,
            ...horizontalDockSx,
            display: 'inline-flex',
          }}
          onMouseEnter={() => setHoverTip(true)}
          onMouseLeave={() => setHoverTip(false)}
        >
          <Fab
            onClick={toggleChat}
            aria-label={isOpen ? CHATBOT_HE.ariaCloseChat : CHATBOT_HE.launcherTooltip}
            sx={{
              width: CHAT_DOCK_FAB_SIZE,
              height: CHAT_DOCK_FAB_SIZE,
              minHeight: CHAT_DOCK_FAB_SIZE,
              bgcolor: isOpen ? '#304fff' : '#ffffff',
              color: isOpen ? '#fff' : '#304fff',
              border: isOpen ? 'none' : '2px solid rgba(48, 79, 255, 0.35)',
              animation: `${fabRingPulse} 2.4s ease-out infinite`,
              '&:hover': {
                bgcolor: isOpen ? '#1e3ad4' : '#fafafa',
              },
            }}
          >
            {isOpen ? (
              <CloseIcon />
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: `${fabFloat} 3.2s ease-in-out infinite`,
                }}
              >
                <Box
                  component="img"
                  src={xtramLogo}
                  alt=""
                  sx={{ width: 36, height: 36, objectFit: 'contain', display: 'block' }}
                />
                <AutoAwesomeIcon
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    fontSize: 20,
                    color: '#304fff',
                    filter: 'drop-shadow(0 0 3px #fff)',
                    animation: `${aiSparkle} 2.8s linear infinite`,
                  }}
                  aria-hidden
                />
              </Box>
            )}
          </Fab>
        </Box>
      </Tooltip>
    </>,
    document.body
  );
}
